<template>
    <div v-if="shouldRenderFooter">
        <div class="footer-info">
            <div class="grid info" :class="{ 'project-list': footerProjects.length }">
                <div class="project-owner">
                    <div class="owner" @click="navigateBack">
                        <div v-if="user?.image?.url" class="owner-picture">
                            <img loading="lazy" height="60" :src="user.image.url" alt="User image" />
                        </div>
                        <div class="owner-info">
                            <div v-if="user?.name" class="owner-name">{{ user.name }}</div>
                            <div v-if="user?.title" class="owner-company">{{ user.title }}</div>
                        </div>
                    </div>
                    <div class="footer-links">
                        <template v-for="page in portfolioPages">
                            <a v-bind:key="page" v-if="page.type === 'file' || page.type === 'mailto' || page.type === 'link'" class="footer-btn" :href="footerHref(page)">
                                {{ page.name }}
                            </a>
                            <a v-bind:key="page.type" v-else class="footer-btn" @click="(evt) => navigateToPage(evt, page)">
                                {{ page.name }}
                            </a>
                        </template>
                    </div>
                </div>
                <div v-if="sortedFooterProjects.length" class="project-owner-projects" :class="{ 'long-list': isLongList }">
                    <div class="title-line">See more of my work:</div>
                    <div class="owner-projects-content">
                        <div
                            v-for="fProject in sortedFooterProjects"
                            :key="fProject.shortId"
                            class="f-project related-project"
                            @click="navigateToProjectFooter(fProject.shortId)"
                        >
                            <div class="image">
                                <ProjectThumbnail
                                    v-if="fProject.settings?.usingGeneratedThumbnail"
                                    :caseStudy="fProject"
                                    force-squared
                                />
                                <img
                                    v-else-if="fProject.image?.url"
                                    loading="lazy"
                                    width="70"
                                    :src="fProject.image.croppedImageUrl || fProject.image.url"
                                    :alt="fProject.name"
                                />
                                <img
                                    v-else-if="fProject.sections?.[0]?.type === 'MainHeader' && fProject.sections[0]?.image"
                                    loading="lazy"
                                    width="70"
                                    :src="fProject.sections[0].image.croppedImageUrl || fProject.sections[0].image.url"
                                    :alt="fProject.name"
                                />
                                <img
                                    v-else
                                    loading="lazy"
                                    width="70"
                                    src="/example_project_images/v2/empty-project-thumbnail.jpg"
                                    :alt="fProject.name"
                                />
                            </div>
                            <div v-if="!isLongList" class="title">
                                <h4>{{ getProjectName(fProject) }}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ProjectThumbnail from "@/scripts/sections/projectGrid/ProjectThumbnail.vue";
import { convertRichTextHTMLToPlainText } from "@/scripts/common/components/textEditor/utilities/textConverterUtilities.js";
import { resolveColor } from "@/scripts/common/helpers.js";
import footer from "@/scripts/view/publicPages/components/Footer.vue";
import {mapGetters} from "vuex";
import {getPortfolioGenerationPreviewPath} from "@/scripts/view/portfolio/generationPreview.js";

export default {
    name: "LegacyCaseStudyFooter",
    components: { ProjectThumbnail },
    computed: {
        ...mapGetters("view", ["viewData", "caseStudyRoute"]),
        footer() {
            return footer
        },
        user() {
            return this.viewData.user;
        },
        portfolio() {
            return this.viewData.portfolio;
        },
        project() {
            return this.viewData.project;
        },
        portfolioPages() {
            return this.viewData.portfolioPages;
        },
        footerProjects() {
            let currentId = this.viewData.activePage;
            let footerP = this.viewData.caseStudies.filter(x => x.shortId !== currentId)
            return footerP || [];
        },
        shouldRenderFooter() {
            const { showFooterOnPortfolio, showFooterOnProjects } = this.portfolio?.settings || {};
            return showFooterOnPortfolio === null && showFooterOnProjects === null;
        },
        sortedFooterProjects() {
            return [...this.footerProjects].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        },
        isLongList() {
            return this.footerProjects.length > 6;
        }
    },
    methods: {
        resolveColor,
        footerHref(page) {
            if (page.type === "file") return page.settings?.link?.url || "#";
            if (page.type === "home") return `/${this.portfolio.shortId}`;
        },
        navigateToPage(evt, page) {
            evt.preventDefault();
            const pageRoute = getPortfolioGenerationPreviewPath(this.$route, page.shortId)
                || (this.rootIsOwnDomain()
                ? `/p/${page.shortId}`
                : `/p/${this.portfolio.shortId}/${page.shortId}`);
            this.$router.push(pageRoute)
        },
        navigateToProjectFooter(projectShortId) {
            const caseStudyRoute = getPortfolioGenerationPreviewPath(this.$route, projectShortId)
                || this.caseStudyRoute(projectShortId);
            if (!caseStudyRoute) return;

            this.$router.push(caseStudyRoute).then(() => {
                window.scrollTo(0, 0);
            });
        },
        rootIsOwnDomain() {
            return this.portfolio.ownDomainAddress && location.origin.includes(this.portfolio.ownDomainAddress);
        },
        navigateBack() {
            const { ownDomainAddress, shortId } = this.portfolio;
            window.location.href = ownDomainAddress
                ? `${window.location.protocol}//${ownDomainAddress}`
                : `${window.location.origin}/${shortId}`;
        },
        getProjectName(project) {
            const header = project.sections?.[0];
            return project.name || convertRichTextHTMLToPlainText(header?.title?.text || "");
        }
    }
};
</script>
